import { useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { api, ApiError, errorMessage } from '../../shared/api/client';
import type { ChurchForm, FormAnswer, FormField, FormResponse } from '../../shared/api/types';
import { Alert, Button, Card, EmptyState, ErrorState, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { APPROVAL_BADGE, APPROVAL_STATUS } from '../approvals/labels';
import { formatAnswer } from './labels';

/** Una respuesta rechazada o retirada no impide volver a responder. */
const counts = (r: FormResponse) => !r.approval || r.approval.status === 'pending' || r.approval.status === 'approved';

/** Responder un formulario abierto. El servidor valida cada respuesta contra las preguntas. */
export function FillFormPage() {
  const { formId = '' } = useParams();
  const me = useMe();
  const churchId = me.activeChurchId;
  const detail = useAsync(
    (signal) =>
      churchId
        ? api.get<{ form: ChurchForm; myResponses: FormResponse[] }>(`/forms/churches/${churchId}/forms/${formId}`, signal)
        : Promise.reject(new Error('Sin contexto')),
    [churchId, formId],
  );

  if (!churchId) return <EmptyState title="Elige primero la iglesia en la que actúas." />;

  return (
    <div className="page page--narrow">
      <p className="small">
        <Link to="/app/formularios">← Formularios</Link>
      </p>
      {detail.status === 'loading' ? <Loading /> : null}
      {detail.status === 'error' ? <ErrorState error={detail.error} onRetry={detail.reload} /> : null}
      {detail.status === 'ready' ? (
        <>
          <header className="page__header">
            <h1>{detail.data.form.title}</h1>
            {detail.data.form.description ? <p className="muted">{detail.data.form.description}</p> : null}
          </header>
          {detail.data.myResponses.map((r) => (
            <Card key={r.id} title="Tu respuesta" action={r.approval ? <span className={APPROVAL_BADGE[r.approval.status]}>{APPROVAL_STATUS[r.approval.status]}</span> : undefined}>
              {r.approval?.note ? <p className="small">“{r.approval.note}”</p> : null}
              <dl className="answers">
                {detail.data.form.fields.map((f) => (
                  <div key={f.key}>
                    <dt>{f.label}</dt>
                    <dd>{formatAnswer(f, r.answers[f.key]) || '—'}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          ))}
          {detail.data.form.status !== 'open' ? <Alert tone="info">Este formulario no está recibiendo respuestas.</Alert> : null}
          {detail.data.form.requiresApproval && detail.data.myResponses.length === 0 ? (
            <Alert tone="info">Tu respuesta quedará por aprobar; te avisaremos cuando se decida.</Alert>
          ) : null}
          {detail.data.form.status === 'open' && !(detail.data.form.onePerPerson && detail.data.myResponses.some(counts)) ? (
            <AnswerCard churchId={churchId} form={detail.data.form} onSent={detail.reload} />
          ) : null}
        </>
      ) : null}
    </div>
  );
}

function AnswerCard({ churchId, form, onSent }: { churchId: string; form: ChurchForm; onSent: () => void }) {
  const [answers, setAnswers] = useState<Record<string, FormAnswer>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; field: string | null } | null>(null);
  const set = (key: string, value: FormAnswer) => setAnswers((a) => ({ ...a, [key]: value }));

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post(`/forms/churches/${churchId}/forms/${form.id}/responses`, { answers });
      setAnswers({});
      onSent();
    } catch (e) {
      const field = e instanceof ApiError && typeof e.details?.field === 'string' ? e.details.field : null;
      setError({ message: errorMessage(e), field });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Tus respuestas">
      <form className="form" onSubmit={onSubmit} noValidate>
        {form.fields.map((f) => (
          <Question key={f.key} field={f} value={answers[f.key]} invalid={error?.field === f.key} onChange={(v) => set(f.key, v)} />
        ))}
        {error ? <Alert>{error.message}</Alert> : null}
        <div className="actions">
          <Button type="submit" busy={busy}>
            Enviar
          </Button>
        </div>
      </form>
    </Card>
  );
}

function Question({ field: f, value, invalid, onChange }: { field: FormField; value: FormAnswer | undefined; invalid: boolean; onChange: (v: FormAnswer) => void }) {
  const id = `q-${f.key}`;
  const label = `${f.label}${f.required ? ' *' : ''}`;
  const common = { id, 'aria-invalid': invalid || undefined, required: f.required };

  if (f.type === 'checkbox') {
    return (
      <label className="choice">
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} aria-invalid={invalid || undefined} />
        <span>{label}</span>
      </label>
    );
  }
  if (f.type === 'choice' || f.type === 'multi') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <fieldset className="choices" aria-invalid={invalid || undefined}>
        <legend>{label}</legend>
        {f.options!.map((o) => (
          <label key={o} className="choice">
            {f.type === 'choice' ? (
              <input type="radio" name={id} checked={value === o} onChange={() => onChange(o)} />
            ) : (
              <input type="checkbox" checked={selected.includes(o)} onChange={(e) => onChange(e.target.checked ? [...selected, o] : selected.filter((s) => s !== o))} />
            )}
            <span>{o}</span>
          </label>
        ))}
      </fieldset>
    );
  }
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      {f.type === 'textarea' ? (
        <textarea {...common} rows={3} maxLength={5000} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input
          {...common}
          type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
          maxLength={f.type === 'text' ? 300 : undefined}
          value={value === undefined ? '' : String(value)}
          onChange={(e) => onChange(f.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
        />
      )}
    </div>
  );
}
